/**
 * Частые вопросы на страницах кодов пополнения — тексты магазина по каждому
 * сервису. Ключи совпадают с CODE_SERVICES. Те же тексты, что на сайте в
 * site/lib/code-faq.ts: правятся в обоих местах.
 */

export interface CodeFaqItem {
  q: string;
  a: string;
}

/** Строки ответа; пустая строка — отступ между абзацами. */
const lines = (...rows: string[]): string => rows.join('\n');

export const CODE_FAQ: Record<string, CodeFaqItem[]> = {
  playstation: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Как активировать код на PS5 или PS4:',
        '1. Откройте PlayStation Store.',
        '2. Перейдите в меню и выберите «Redeem Codes» / «Погасить коды».',
        '3. Введите 12-значный код.',
        '4. Проверьте данные и подтвердите активацию.',
        '',
        'Как активировать код через сайт или приложение PlayStation App:',
        '1. Войдите в свой аккаунт PSN.',
        '2. Нажмите на свою аватарку сверху справа.',
        '3. Выберите раздел «Redeem Codes» / «Погасить коды».',
        '4. Введите 12-значный код.',
        '5. Подтвердите активацию.',
        '',
        'Важно: коды PlayStation обычно привязаны к региону аккаунта PSN. Например, код для Турции нужно активировать на турецком аккаунте, код для Индии — на индийском аккаунте.',
      ),
    },
    {
      q: 'Что такое код пополнения PlayStation?',
      a: lines(
        'Код пополнения PlayStation — это цифровой код, который пополняет кошелек PlayStation Store.',
        '',
        'После активации баланс можно использовать для покупки игр, дополнений, подписок и другого контента в PlayStation Store.',
      ),
    },
    {
      q: 'Почему важен регион аккаунта PlayStation?',
      a: lines(
        'Коды PlayStation обычно привязаны к региону аккаунта PSN.',
        '',
        'Например, код для Турции нужно активировать на турецком аккаунте, а код для Индии — на индийском аккаунте. Если регион не совпадает, код может не активироваться.',
      ),
    },
    {
      q: 'Что делать, если код PlayStation не активируется?',
      a: lines(
        'Проверьте правильность ввода кода, регион аккаунта и убедитесь, что код ранее не был использован.',
        '',
        'Если данные верные, но код все равно не активируется, обратитесь в поддержку.',
      ),
    },
  ],

  apple: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Код пополнения Apple зачисляется на баланс Apple Account. После активации средства можно использовать для покупок в App Store, оплаты подписок Apple и других доступных сервисов Apple.',
        '',
        'Как активировать код на iPhone или iPad:',
        '1. Откройте App Store.',
        '2. Нажмите на иконку профиля в правом верхнем углу.',
        '3. Выберите «Погасить подарочную карту или код».',
        '4. Введите код вручную или используйте камеру.',
        '5. Подтвердите активацию.',
        '',
        'Как активировать код на Mac:',
        '1. Откройте App Store.',
        '2. Нажмите на имя профиля или войдите в Apple Account.',
        '3. Выберите «Погасить подарочную карту».',
        '4. Введите код и подтвердите активацию.',
        '',
        'Важно: перед покупкой убедитесь, что регион кода совпадает с регионом вашего Apple Account. Коды Apple обычно привязаны к стране или региону аккаунта.',
      ),
    },
    {
      q: 'Что такое код пополнения Apple?',
      a: lines(
        'Код пополнения Apple — это цифровой код, который пополняет баланс вашего Apple Account.',
        '',
        'После активации баланс можно использовать для покупок в App Store, оплаты подписок Apple и других доступных сервисов Apple.',
      ),
    },
    {
      q: 'Почему важен регион аккаунта Apple?',
      a: lines(
        'Коды Apple обычно привязаны к конкретной стране или региону.',
        '',
        'Если регион кода не совпадает с регионом вашего Apple Account, код может не активироваться. Перед покупкой обязательно проверьте регион аккаунта.',
      ),
    },
    {
      q: 'Можно ли подарить код Apple другому человеку?',
      a: lines(
        'Да, можно, если код еще не был активирован.',
        '',
        'Главное — убедиться, что регион кода совпадает с регионом Apple Account человека, которому вы хотите его передать.',
      ),
    },
    {
      q: 'Что делать, если код Apple не активируется?',
      a: lines(
        'Проверьте правильность ввода кода, регион Apple Account и убедитесь, что код ранее не был активирован.',
        '',
        'Если данные указаны верно, но код все равно не активируется, напишите нам в чат — поможем разобраться.',
      ),
    },
  ],

  nintendo: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Как активировать код на Nintendo Switch:',
        '1. Откройте Nintendo eShop на консоли.',
        '2. Выберите аккаунт, на который хотите активировать код.',
        '3. В меню слева выберите «Redeem Code» / «Активировать код».',
        '4. Введите 16-значный код.',
        '5. Подтвердите активацию.',
        '',
        'Как активировать код через сайт:',
        '1. Перейдите на страницу активации кода Nintendo.',
        '2. Войдите в нужный Nintendo Account.',
        '3. Введите 16-значный код.',
        '4. Проверьте аккаунт и подтвердите активацию.',
        '',
        'Важно: код нужно активировать на аккаунте нужного региона. Если регион кода и аккаунта не совпадают, активация может не пройти.',
      ),
    },
    {
      q: 'Что такое код пополнения Nintendo?',
      a: lines(
        'Код пополнения Nintendo — это цифровой код, который пополняет баланс Nintendo eShop или активирует цифровой товар, если это не код баланса.',
        '',
        'После активации средства можно использовать для покупок в Nintendo eShop.',
      ),
    },
    {
      q: 'Почему важен регион аккаунта Nintendo?',
      a: lines(
        'Коды Nintendo могут быть привязаны к региону.',
        '',
        'Если регион кода и регион аккаунта не совпадают, активация может не пройти. Перед покупкой проверьте, для какого региона предназначен код.',
      ),
    },
    {
      q: 'Что делать, если код Nintendo не активируется?',
      a: lines(
        'Проверьте правильность ввода кода, регион аккаунта и убедитесь, что код ранее не был использован.',
        '',
        'Если данные верные, но код все равно не активируется, обратитесь в поддержку.',
      ),
    },
  ],

  roblox: [
    {
      q: 'Как активировать код?',
      a: lines(
        '1. Перейдите на сайт roblox.com/redeem.',
        '2. Войдите в нужный аккаунт Roblox.',
        '3. Введите код в поле активации.',
        '4. Нажмите «Redeem» / «Активировать».',
        '5. После успешной активации баланс появится на аккаунте.',
        '',
        'Важно: перед активацией проверьте, что вы вошли именно в тот аккаунт Roblox, на который хотите зачислить код.',
      ),
    },
    {
      q: 'Что такое код пополнения Roblox?',
      a: lines(
        'Код пополнения Roblox — это цифровой код, который активируется на аккаунте Roblox.',
        '',
        'После активации код добавляет Robux на аккаунт.',
      ),
    },
    {
      q: 'Почему важно войти в правильный аккаунт Roblox?',
      a: lines(
        'После активации код зачисляется на тот аккаунт, в который вы вошли.',
        '',
        'Перенести баланс или Robux на другой аккаунт после активации обычно невозможно. Перед вводом кода обязательно проверьте аккаунт.',
      ),
    },
    {
      q: 'Можно ли подарить код Roblox другу?',
      a: lines(
        'Да, можно, если код еще не был активирован.',
        '',
        'Друг должен самостоятельно войти в свой аккаунт Roblox и активировать код на странице активации.',
      ),
    },
    {
      q: 'Что делать, если код Roblox не активируется?',
      a: lines(
        'Проверьте правильность ввода кода и убедитесь, что он ранее не был использован.',
        '',
        'Если код введен верно, но активация не проходит, напишите нам в чат — поможем разобраться.',
      ),
    },
  ],

  gplay: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Код Google Play пополняет баланс Google Play. После активации средства можно использовать для покупок в Google Play: приложений, игр, внутриигровых покупок, подписок и другого доступного контента.',
        '',
        'Как активировать код на Android:',
        '1. Откройте приложение Google Play.',
        '2. Нажмите на иконку профиля в правом верхнем углу.',
        '3. Перейдите в «Платежи и подписки».',
        '4. Выберите «Использовать код» / «Redeem code».',
        '5. Введите код.',
        '6. Проверьте аккаунт и подтвердите активацию.',
        '',
        'Как активировать код через сайт:',
        '1. Перейдите на страницу активации Google Play.',
        '2. Войдите в нужный Google аккаунт.',
        '3. Введите код.',
        '4. Подтвердите активацию.',
        '',
        'Важно: коды Google Play обычно привязаны к стране аккаунта Google Play. Если регион кода и аккаунта не совпадают, активация может не пройти.',
        '',
        'Погашение кода осуществляется силами покупателя. Если код не удается погасить и Google Play выдает ошибку GPS-RCPM-02, магазин ответственности не несет.',
      ),
    },
    {
      q: 'Что такое код пополнения Google Play?',
      a: lines(
        'Код пополнения Google Play — это цифровой код, который пополняет баланс Google Play.',
        '',
        'После активации баланс можно использовать для покупки приложений, игр, внутриигровых покупок, подписок и другого доступного контента.',
      ),
    },
    {
      q: 'Почему важен регион аккаунта Google Play?',
      a: lines(
        'Коды Google Play привязаны к стране аккаунта.',
        '',
        'Если регион кода и регион аккаунта Google Play не совпадают, код может не активироваться. Перед покупкой обязательно проверьте регион аккаунта.',
      ),
    },
    {
      q: 'Можно ли активировать код Google Play на другом аккаунте?',
      a: lines(
        'Да, если код еще не был активирован и подходит для региона этого аккаунта.',
        '',
        'После активации баланс зачисляется на выбранный аккаунт Google Play, и перенести его на другой аккаунт обычно нельзя.',
      ),
    },
    {
      q: 'Что делать, если код Google Play не активируется?',
      a: lines(
        'Проверьте правильность ввода кода, регион аккаунта и убедитесь, что код ранее не был использован.',
        '',
        'Погашение кода осуществляется силами покупателя. Если Google Play выдает ошибку GPS-RCPM-02, магазин ответственности за такой код не несет.',
      ),
    },
  ],

  blizzard: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Код Blizzard пополняет баланс Battle.net. После активации баланс можно использовать для покупок в Battle.net: игр, дополнений, внутриигровых товаров и других доступных продуктов Blizzard.',
        '',
        'Как активировать код Blizzard / Battle.net:',
        '1. Перейдите на страницу активации кода Battle.net.',
        '2. Войдите в нужный аккаунт Battle.net.',
        '3. Введите код в поле активации.',
        '4. Проверьте данные и подтвердите активацию.',
        '5. После успешной активации сумма будет добавлена на баланс Battle.net.',
        '',
        'Важно: коды Blizzard могут иметь региональные ограничения. Перед покупкой убедитесь, что код подходит для региона вашего Battle.net аккаунта.',
      ),
    },
    {
      q: 'Что такое код пополнения Blizzard?',
      a: lines(
        'Код пополнения Blizzard — это цифровой код, который пополняет баланс Battle.net.',
        '',
        'После активации баланс можно использовать для покупки игр, дополнений, внутриигровых товаров и другого доступного контента Blizzard.',
      ),
    },
    {
      q: 'Почему важен регион аккаунта Battle.net?',
      a: lines(
        'Коды Blizzard могут иметь региональные ограничения.',
        '',
        'Если код не подходит для региона вашего аккаунта Battle.net, активация может не пройти. Перед покупкой обязательно проверьте регион товара.',
      ),
    },
    {
      q: 'Можно ли подарить код Blizzard другу?',
      a: lines(
        'Да, можно, если код еще не был активирован.',
        '',
        'Перед передачей кода убедитесь, что он подходит для региона аккаунта Battle.net вашего друга.',
      ),
    },
  ],

  fortnite: [
    {
      q: 'Как активировать код?',
      a: lines(
        '1. Откройте официальный сайт Fortnite и войдите под своей учетной записью.',
        '2. В правом верхнем углу нажмите на «Еще» и далее «В-Баксы».',
        '3. Нажмите на кнопку «Начать».',
        '4. Вставьте код и нажмите «Далее».',
        '5. Выберите, где вы хотите использовать В-Баксы.',
        '6. Нажмите на кнопку «Подтвердить».',
        '7. Готово. Вы успешно задонатили в Fortnite!',
      ),
    },
    {
      q: 'Что такое код пополнения Fortnite?',
      a: lines(
        'Код пополнения Fortnite — это цифровой код, который активируется на аккаунте Fortnite.',
        '',
        'После активации код добавляет баланс на аккаунт.',
      ),
    },
    {
      q: 'Почему важно войти в правильный аккаунт Fortnite?',
      a: lines(
        'После активации код зачисляется на тот аккаунт, в который вы вошли.',
        '',
        'Перенести баланс на другой аккаунт после активации обычно невозможно. Перед вводом кода обязательно проверьте аккаунт.',
      ),
    },
    {
      q: 'Можно ли подарить код Fortnite другу?',
      a: lines(
        'Да, можно, если код еще не был активирован.',
        '',
        'Друг должен самостоятельно войти в свой аккаунт Fortnite и активировать код на странице активации.',
      ),
    },
    {
      q: 'Что делать, если код Fortnite не активируется?',
      a: lines(
        'Проверьте правильность ввода кода и убедитесь, что он ранее не был использован.',
        '',
        'Если код введен верно, но активация не проходит, напишите нам в чат — поможем разобраться.',
      ),
    },
  ],

  valorant: [
    {
      q: 'Как активировать код?',
      a: lines(
        'Пошаговая инструкция по активации подарочной карты Valorant:',
        '1. Зайдите в приложение, скачанное с официального сайта Valorant, и войдите под своей учетной записью.',
        '2. Слева в списке нажмите на «Магазин».',
        '3. В правом верхнем углу нажмите на иконку валюты — Valorant Points.',
        '4. Выберите способ оплаты «Коды», затем в специальное поле вставьте код и кликните «Отправить».',
        '5. Готово.',
      ),
    },
    {
      q: 'Что такое код пополнения Valorant?',
      a: lines(
        'Код пополнения Valorant — это цифровой код, который активируется на аккаунте Valorant.',
        '',
        'После активации код добавляет баланс на аккаунт.',
      ),
    },
    {
      q: 'Почему важно войти в правильный аккаунт Valorant?',
      a: lines(
        'После активации код зачисляется на тот аккаунт, в который вы вошли.',
        '',
        'Перенести баланс на другой аккаунт после активации обычно невозможно. Перед вводом кода обязательно проверьте аккаунт.',
      ),
    },
    {
      q: 'Можно ли подарить код Valorant другу?',
      a: lines(
        'Да, можно, если код еще не был активирован.',
        '',
        'Друг должен самостоятельно войти в свой аккаунт Valorant и активировать код на странице активации.',
      ),
    },
    {
      q: 'Что делать, если код Valorant не активируется?',
      a: lines(
        'Проверьте правильность ввода кода и убедитесь, что он ранее не был использован.',
        '',
        'Если код введен верно, но активация не проходит, напишите нам в чат — поможем разобраться.',
      ),
    },
  ],
};
